import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { buildReportConfigFromCRMView } from 'reporting-data/createReportConfig/createReportUtils';
import useFetchPropertyMetaDataForDataType from 'reporting-data/hooks/useFetchPropertyMetaDataForDataType';
import { useSelectedObjectTypeId } from '../../objectTypeIdContext/hooks/useSelectedObjectTypeId';
import { useSearchTerm } from '../../rewrite/search/hooks/useSearchTerm';
import { useQueryFilterGroups } from '../../rewrite/searchQuery/hooks/useQueryFilterGroups';
import { useCurrentView } from '../../views/hooks/useCurrentView';
import { useCurrentViewId } from '../../views/hooks/useCurrentViewId';
import { getMostRecentlyUsedReport, setMostRecentlyUsedReport } from '../utils/mostRecentlyUsedReport';
import { jsx as _jsx } from "react/jsx-runtime";
const ReportToRenderContext = /*#__PURE__*/createContext(null);
export const ReportToRenderProvider = ({
  children
}) => {
  const currentView = useCurrentView();
  const viewId = useCurrentViewId();
  const viewFilterGroups = useQueryFilterGroups();
  const objectTypeId = useSelectedObjectTypeId();
  const searchTerm = useSearchTerm();
  const propertyMetadataFetch = useFetchPropertyMetaDataForDataType(objectTypeId);
  const [reportOverride, setReportOverride] = useState(undefined);
  const [reportToRender, setReportToRender] = useState();
  const previousViewFilters = useRef();
  const previousSearchTerm = useRef();
  useEffect(() => {
    var _currentView$columns$;
    if (previousViewFilters.current && JSON.stringify(previousViewFilters.current) === JSON.stringify(viewFilterGroups) && previousSearchTerm.current === searchTerm || !isSucceeded(propertyMetadataFetch)) {
      return;
    }
    previousViewFilters.current = viewFilterGroups;
    previousSearchTerm.current = searchTerm;
    const columns = (_currentView$columns$ = currentView === null || currentView === void 0 ? void 0 : currentView.columns.map(col => Object.assign({}, col))) !== null && _currentView$columns$ !== void 0 ? _currentView$columns$ : [];
    const buildReport = async () => {
      const mostRecentlyUsedReport = await getMostRecentlyUsedReport(objectTypeId, viewId, (currentView === null || currentView === void 0 ? void 0 : currentView.name) || 'CRM view report');
      const report = buildReportConfigFromCRMView({
        startingReport: mostRecentlyUsedReport || reportOverride,
        propertyMetadata: propertyMetadataFetch.data,
        objectTypeId,
        columns,
        filters: viewFilterGroups,
        viewName: (currentView === null || currentView === void 0 ? void 0 : currentView.name) || 'CRM view report',
        searchTerm
      });
      if (!reportOverride) {
        setReportOverride(report);
      }
      setMostRecentlyUsedReport(report, objectTypeId, viewId);
      setReportToRender(report);
    };
    void buildReport();
  }, [currentView, objectTypeId, viewFilterGroups, reportOverride, propertyMetadataFetch, viewId, searchTerm]);
  const handlePreviewReportChange = useCallback(reportingUpdatedReport => {
    setReportOverride(reportingUpdatedReport);
    setMostRecentlyUsedReport(reportingUpdatedReport, objectTypeId, viewId);
    setReportToRender(reportingUpdatedReport);
  }, [objectTypeId, viewId]);
  const value = useMemo(() => ({
    reportToRender,
    handlePreviewReportChange
  }), [reportToRender, handlePreviewReportChange]);
  return /*#__PURE__*/_jsx(ReportToRenderContext.Provider, {
    value: value,
    children: children
  });
};
export const useReportToRender = () => {
  const context = useContext(ReportToRenderContext);
  if (!context) {
    throw new Error('useReportToRender must be used within a ReportToRenderProvider');
  }
  return context;
};